import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../styles/Certificate.css';

const Certificate = () => {
  const { certificateId } = useParams();
  const [certificateData, setCertificateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = React.useRef(null);

  // Determine backend origin based on environment (local vs prod)
  const computedOrigin = React.useMemo(() => {
    // Allow override via env var if provided at build time
    const envBase = process.env.REACT_APP_API_BASE_URL;
    if (envBase) return envBase.endsWith('/') ? envBase : `${envBase}/`;
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    return isLocal ? 'http://localhost:8000/' : 'https://api.dataepic.dev/';
  }, []);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        setLoading(true);
        const url = `${computedOrigin}certificate/${certificateId}/`;
        const response = await fetch(url);
        
        if (!response.ok) {
          const text = await response.text().catch(() => '');
          const msg = `Request to ${url} failed: ${response.status} ${response.statusText}`;
          // Log server response for debugging in DevTools
          // eslint-disable-next-line no-console
          console.error(msg, text);
          throw new Error(response.status === 404 ? 'Certificate not found' : msg);
        }
        
        const html = await response.text();
        // Inject a <base> tag so relative assets resolve to the backend domain
        const backendOrigin = computedOrigin;
        let patched = html;
        if (/<head[^>]*>/i.test(patched)) {
          patched = patched.replace(/<head[^>]*>/i, (m) => `${m}\n  <base href="${backendOrigin}">`);
        } else {
          // If no <head>, prepend one to ensure assets resolve
          patched = `<!DOCTYPE html><html><head><base href="${backendOrigin}"></head><body>${patched}</body></html>`;
        }
        setCertificateData(patched);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (certificateId) {
      fetchCertificate();
    }
  }, [certificateId, computedOrigin]);

  const handlePrint = () => {
    // Print the iframe content so only the certificate is printed
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
    } else {
      window.print();
    }
  };

  const handleDownloadPDF = () => {
    window.open(`${computedOrigin}certificate/${certificateId}/download/`, '_blank');
  };

  if (loading) {
    return (
      <div className="certificate-container">
        <div className="loading">
          <h2>Loading Certificate...</h2>
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="certificate-container">
        <div className="error">
          <h2>Certificate Not Found</h2>
          <p>{error}</p>
          <p>Please check the certificate ID and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="certificate-container">
      <div className="certificate-actions">
        <button onClick={handlePrint} className="btn btn-primary">
          Print Certificate
        </button>
        <button onClick={handleDownloadPDF} className="btn btn-secondary">
          Download PDF
        </button>
      </div>
      {/* Render certificate HTML in an iframe to isolate styles and resolve assets via <base> */}
      <iframe
        ref={iframeRef}
        title="Data Epic Certificate"
        className="certificate-content"
        srcDoc={certificateData || ''}
        style={{ width: '100%', border: '0', minHeight: '80vh' }}
        onLoad={() => {
          try {
            const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
            if (!doc) return;
            // Auto-size to the certificate content
            const setHeight = () => {
              const body = doc.body;
              const html = doc.documentElement;
              const height = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
              iframeRef.current.style.height = `${Math.max(600, height)}px`;
            };
            setHeight();
            // Recompute height after assets load
            Array.from(doc.images || []).forEach(img => {
              img.addEventListener('load', setHeight);
            });
          } catch (_) {
            // Cross-origin guard (should not happen with srcDoc)
          }
        }}
      />
    </div>
  );
};

export default Certificate;
